import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Agent } from '@/hooks/useAgents';
import { PartialAsset } from '@/hooks/useAssets';
import { Capability } from '@/hooks/useCapabilities';
import {
  EntityBlock,
  RiskWithVulnerability,
} from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import {
  Account,
  IntegrationMeta,
  ParkedDomain,
  Preseed,
  Risk,
  Seed,
  Technology,
  Vulnerability,
} from '@/types';
import { safeExecute } from '@/utils/function.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

interface UseModalState {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface GlobalState {
  modal: {
    allModal: UseModalState;
    seed: UseModalState;
    risk: {
      open: boolean;
      type: 'risk' | 'material' | 'selectorScreen';
      onChange: (
        open: boolean,
        type: GlobalState['modal']['risk']['type']
      ) => void;
      selectedAssets: string[];
      onSelectedAssetsChange: React.Dispatch<React.SetStateAction<string[]>>;
      selectedRisks: string[];
      onSelectedRisksChange: React.Dispatch<React.SetStateAction<string[]>>;
    };
    selectUserAgent: {
      assetKeys: string[];
      onAssetKeysChange: React.Dispatch<React.SetStateAction<string[]>>;
    };
    asset: UseModalState;
    file: UseModalState;
    upgrade: UseModalState;
    pushNotification: UseModalState;
    surfaceSetup: UseModalState;
    alert: UseModalState;
    ticket: {
      associateTicket: UseModalState & {
        riskKeys: string[];
        onRiskKeysChange: React.Dispatch<React.SetStateAction<string[]>>;
      };
      setup: UseModalState;
    };
  };
  awsMarketplaceConfig: {
    value?: Record<string, string>;
    onChange: React.Dispatch<GlobalState['awsMarketplaceConfig']['value']>;
    verifyLinking: boolean;
    onVerifyLinkingChange: React.Dispatch<React.SetStateAction<boolean>>;
  };
  riskNotification: {
    value: { message: string } | null;
    onChange: (value: { message: string } | null) => void;
  };
  setupIntegration: {
    value: Account | undefined;
    onChange: React.Dispatch<React.SetStateAction<Account | undefined>>;
  };
  dashboard: {
    period: string;
  };
  drawerState: {
    drawerOrder: (
      | 'agent'
      | 'asset'
      | 'vulnerability'
      | 'seed'
      | 'integration'
      | 'threatIntel'
      | 'preseed'
      | 'technology'
      | 'capability'
      | 'queryResults'
      | 'customer'
      | 'parkedDomain'
    )[];
    agent?: Agent;
    vulnerability?: Risk;
    seed?: Seed;
    integration?: IntegrationMeta;
    threatIntel?: Vulnerability;
    preseed?: Preseed;
    capability?: Capability;
    customer?: import('@/sections/customerManagement/types').CustomerData;
    parkedDomain?: ParkedDomain;
    queryResults?: RiskWithVulnerability[];
    queryBlocks?: EntityBlock[];
    queryCollaborators?: Array<{ username: string; displayName?: string }>;
    includeCustomerColumn?: boolean;
    queryPaginationControls?: {
      fetchNextPage: () => void;
      hasNextPage: boolean;
      isFetchingNextPage: boolean;
      totalCount?: number;
    };
    querySortControls?: {
      orderBy: string;
      descending: boolean;
      onSortChange: (
        sortConfig: { orderBy: string; descending: boolean } | null
      ) => void;
    };
    queryRefetch?: () => void;

    // Properties
    agentDrawerKey?: string;
    agentDrawerTab?: string;
    assetDrawerTab?: string;
    assetDrawerSubTab?: string;
    assetDrawerKey?: string;
    technologyDrawerKey?: string;
    technologyDrawerTab?: string;
    vulnerabilityDrawerTab?: string;
    vulnerabilityDrawerKey?: string;
    seedDrawerKey?: string;
    seedDrawerTab?: string;
    integrationDrawerKey?: string;
    integrationDrawerCategory?: string;
    notificationsDrawerOpen?: boolean;
    notificationsDrawerFilterType?: 'email' | 'webhook' | 'integration';
    exposuresDrawerOpen?: boolean;
    threatIntelDrawerKey?: string;
    preseedDrawerKey?: string;
    preseedDrawerTab?: string;
    capabilityDrawerKey?: string;
    capabilityDrawerTab?: string;
    customerDrawerKey?: string;
    customerDrawerTab?: string;
    queryResultsDrawerKey?: string;
    parkedDomainDrawerKey?: string;

    // Setters
    setAgent: (agent?: Agent) => void;
    setAgentDrawerTab: (agentDrawerTab: string) => void;
    setAssetDrawerTab: (assetDrawerTab: string) => void;
    setTechnologyDrawerTab: (technologyDrawerTab: string) => void;
    setVulnerability: (vulnerability?: Risk) => void;
    setVulnerabilityDrawerTab: (vulnerabilityDrawerTab: string) => void;
    setSeed: (seed?: Seed) => void;
    setSeedDrawerTab: (seedDrawerTab: string) => void;
    setIntegration: (integration?: IntegrationMeta) => void;
    setIntegrationDrawerCategory: (category: string) => void;
    setNotificationsDrawerOpen: (
      notificationsDrawerOpen: boolean,
      filterType?: 'email' | 'webhook' | 'integration'
    ) => void;
    setExposuresDrawerOpen: (exposuresDrawerOpen: boolean) => void;
    setPreseed: (preseed?: Preseed) => void;
    setThreatIntel: (threatIntel?: Vulnerability) => void;
    setCapability: (capability?: Capability) => void;
    setCustomer: (
      customer?: import('@/sections/customerManagement/types').CustomerData
    ) => void;
    setCustomerDrawerTab: (customerDrawerTab: string) => void;
    setParkedDomain: (parkedDomain?: ParkedDomain) => void;
    setQueryResults: (results?: RiskWithVulnerability[]) => void;
    setQueryBlocks: (blocks?: EntityBlock[]) => void;
    setQueryPaginationControls: (controls?: {
      fetchNextPage: () => void;
      hasNextPage: boolean;
      isFetchingNextPage: boolean;
      totalCount?: number;
    }) => void;
    setQuerySortControls: (controls?: {
      orderBy: string;
      descending: boolean;
      onSortChange: (
        sortConfig: { orderBy: string; descending: boolean } | null
      ) => void;
    }) => void;

    // Handlers
    handleOpenAgentDrawer: (
      agent?: Agent,
      options?: { initialTab?: string }
    ) => void;
    handleOpenTechnologyDrawer: (technology?: Technology) => void;
    handleOpenAssetDrawer: (
      asset?: PartialAsset,
      isActiveDirectory?: boolean
    ) => void;
    handleOpenVulnerabilityDrawer: (
      vulnerability?: Risk,
      doNotSetVulnerability?: boolean
    ) => void;
    handleOpenSeedDrawer: (seed?: Seed) => void;
    handleOpenIntegrationDrawer: (
      integration?: IntegrationMeta,
      category?: string
    ) => void;
    handleOpenThreatIntelDrawer: (threatIntel?: Vulnerability) => void;
    handleOpenPreseedDrawer: (preseed?: Preseed) => void;
    handleOpenCapabilityDrawer: (capability?: Capability) => void;
    handleOpenCustomerDrawer: (
      customer?: import('@/sections/customerManagement/types').CustomerData,
      options?: { initialTab?: string }
    ) => void;
    handleOpenParkedDomainDrawer: (parkedDomain?: ParkedDomain) => void;
    handleOpenQueryResultsDrawer: (
      results?: RiskWithVulnerability[],
      queryBlocks?: EntityBlock[],
      paginationControls?: {
        fetchNextPage: () => void;
        hasNextPage: boolean;
        isFetchingNextPage: boolean;
        totalCount?: number;
      },
      sortControls?: {
        orderBy: string;
        descending: boolean;
        onSortChange: (
          sortConfig: { orderBy: string; descending: boolean } | null
        ) => void;
      },
      refetchQuery?: () => void,
      collaborators?: Array<{ username: string; displayName?: string }>,
      includeCustomerColumn?: boolean
    ) => void;
  };
  lastVisitedUserId: string;
  setLastVisitedUserId: React.Dispatch<React.SetStateAction<string>>;
  keyboardListeners: {
    add: (
      id: string,
      listeners: Record<string, (event: KeyboardEvent) => void>,
      altKey?: boolean
    ) => void;
    remove: (id: string) => void;
    setPOEEnabled: (active: boolean) => void;
    poeEnabled: boolean;
  };
}

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};

export const GlobalStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const period = '90';

  const [allModalOpen, setAllModalOpen] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [riskType, setRiskType] =
    useState<GlobalState['modal']['risk']['type']>('selectorScreen');

  const [selectUserAgentAssetKeys, setSelectUserAgentAssetKeys] = useState<
    string[]
  >([]);

  const [assetOpen, setAssetOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [pushNotificationOpen, setPushNotificationOpen] = useState(false);
  const [surfaceSetupOpen, setSurfaceSetupOpen] = useState(false);
  const [integration, setIntegration] = useState<IntegrationMeta>();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [setupIntegration, setSetupIntegration] = useState<Account>();
  const [poeEnabled, setPOEEnabled] = useState(true);

  const [riskNotification, setRiskNotification] = useState<{
    message: string;
  } | null>(null);

  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedRisks, setSelectedRisks] = useState<string[]>([]);

  const [awsMarketplaceConfig, setAwsMarketplaceConfig] = useStorage<
    GlobalState['awsMarketplaceConfig']['value']
  >({
    key: StorageKey.AWS_MARKETPLACE_CONFIG,
  });
  const [verifyLinking, setVerifyLinking] = useStorage<
    GlobalState['awsMarketplaceConfig']['verifyLinking']
  >(
    {
      key: StorageKey.AWS_MARKETPLACE_CONFIG_VERIFY_LINKING,
    },
    false
  );
  const [listeners, setListeners] = useState<{
    [key: string]: Array<{
      id: string;
      callback: (event: KeyboardEvent) => void;
      altKey?: boolean;
    }>;
  }>({});

  const [searchParams, setSearchParams] = useSearchParams();

  const drawerOrder = safeExecute<
    (
      | 'agent'
      | 'asset'
      | 'vulnerability'
      | 'seed'
      | 'integration'
      | 'threatIntel'
      | 'preseed'
      | 'technology'
      | 'capability'
      | 'queryResults'
      | 'customer'
      | 'parkedDomain'
    )[]
  >(() => {
    return JSON.parse(searchParams.get('drawerOrder') || '[]');
  }, []);
  const agentDrawerKey = searchParams.get('agentDrawerKey') || '';
  const agentDrawerTab = searchParams.get('agentDrawerTab') || '';
  const assetDrawerTab = searchParams.get('assetDrawerTab') || '';
  const technologyDrawerTab = searchParams.get('technologyDrawerTab') || '';
  const assetDrawerSubTab = searchParams.get('assetDrawerSubTab') || '';
  const assetDrawerKey = searchParams.get('assetDrawerKey') || '';
  const technologyDrawerKey = searchParams.get('technologyDrawerKey') || '';
  const vulnerabilityDrawerTab =
    searchParams.get('vulnerabilityDrawerTab') || '';
  const vulnerabilityDrawerKey =
    searchParams.get('vulnerabilityDrawerKey') || '';
  const threatIntelDrawerKey = searchParams.get('threatIntelDrawerKey') || '';
  const preseedDrawerKey = searchParams.get('preseedDrawerKey') || '';
  const preseedDrawerTab = searchParams.get('preseedDrawerTab') || '';
  const seedDrawerKey = searchParams.get('seedDrawerKey') || '';
  const seedDrawerTab = searchParams.get('seedDrawerTab') || '';
  const capabilityDrawerKey = searchParams.get('capabilityDrawerKey') || '';
  const capabilityDrawerTab = searchParams.get('capabilityDrawerTab') || '';
  const customerDrawerKey = searchParams.get('customerDrawerKey') || '';
  const customerDrawerTab = searchParams.get('customerDrawerTab') || '';
  const parkedDomainDrawerKey = searchParams.get('parkedDomainDrawerKey') || '';
  const queryResultsDrawerKey = searchParams.get('queryResultsDrawerKey') || '';

  const [associateTicketRiskKeys, setAssociateTicketRiskKeys] = useState<
    string[]
  >([]);
  const [associateTicketOpen, setAssociateTicketOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  const [agent, setAgent] = useState<Agent>();
  const [vulnerability, setVulnerability] = useState<Risk>();
  const [seed, setSeed] = useState<Seed>();
  const [threatIntel, setThreatIntel] = useState<Vulnerability>();
  const [preseed, setPreseed] = useState<Preseed>();
  const [capability, setCapability] = useState<Capability>();
  const [customer, setCustomer] =
    useState<import('@/sections/customerManagement/types').CustomerData>();
  const [parkedDomain, setParkedDomain] = useState<ParkedDomain>();
  const [queryResults, setQueryResults] = useState<RiskWithVulnerability[]>();
  const [queryBlocks, setQueryBlocks] = useState<EntityBlock[]>();
  const [queryRefetch, setQueryRefetch] = useState<(() => void) | undefined>();
  const [queryCollaborators, setQueryCollaborators] =
    useState<Array<{ username: string; displayName?: string }>>();
  const [includeCustomerColumn, setIncludeCustomerColumn] =
    useState<boolean>(false);
  const [queryPaginationControls, setQueryPaginationControls] = useState<
    | {
        fetchNextPage: () => void;
        hasNextPage: boolean;
        isFetchingNextPage: boolean;
        totalCount?: number;
      }
    | undefined
  >();
  const [querySortControls, setQuerySortControls] = useState<
    | {
        orderBy: string;
        descending: boolean;
        onSortChange: (
          sortConfig: { orderBy: string; descending: boolean } | null
        ) => void;
      }
    | undefined
  >();
  const [notificationsDrawerOpen, setNotificationsDrawerOpenState] =
    useState(false);
  const [notificationsDrawerFilterType, setNotificationsDrawerFilterType] =
    useState<'email' | 'webhook' | 'integration' | undefined>(undefined);
  const [exposuresDrawerOpen, setExposuresDrawerOpen] = useState(false);

  // Wrapper to set both drawer open state and filter type
  const setNotificationsDrawerOpen = (
    open: boolean,
    filterType?: 'email' | 'webhook' | 'integration'
  ) => {
    setNotificationsDrawerOpenState(open);
    setNotificationsDrawerFilterType(open ? filterType : undefined);
  };
  const [lastVisitedUserId, setLastVisitedUserId] = useStorage<string>(
    {
      key: StorageKey.LAST_VISITED_USER_ID,
    },
    ''
  );

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!listeners) return;

      const options = listeners[`${event.code}${event.altKey ? '-Alt' : ''}`];
      if (options && options.length > 0) {
        const { callback } = options[options.length - 1];
        callback?.(event);
      }
    }

    if (!allModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [listeners, allModalOpen]);

  const addKeyDownListeners = useCallback(
    (
      id: string,
      listeners: {
        [key: string]: (event: KeyboardEvent) => void;
      },
      altKey = false
    ) => {
      setListeners(prevListeners => {
        const newListeners = { ...prevListeners };
        Object.entries(listeners).forEach(([keyCode, callback]) => {
          const key = `${keyCode}${altKey ? '-Alt' : ''}`;
          if (newListeners[key]) {
            const existingListenerIndex = newListeners[key].findIndex(
              listener => listener.id === id
            );
            const existingListener = newListeners[key][existingListenerIndex];
            if (existingListenerIndex > -1) {
              existingListener.callback = callback;
            } else {
              newListeners[key].push({ id, callback });
            }
          } else {
            newListeners[key] = [{ id, callback }];
          }
        });
        return newListeners;
      });
    },
    []
  );

  const removeKeyDownListeners = useCallback((id: string) => {
    setListeners(prevListeners => {
      const newListeners = { ...prevListeners };

      Object.entries(newListeners).forEach(([key, callbackObjects]) => {
        newListeners[key] = callbackObjects.filter(
          obj => obj.id.split('-')[0] !== id
        );
      });

      return newListeners;
    });
  }, []);

  function handleSetSearchParams({
    drawerType,
    tabKeys = [],
    resource,
    defaultValues = {},
  }: {
    drawerType:
      | 'agent'
      | 'asset'
      | 'vulnerability'
      | 'seed'
      | 'threatIntel'
      | 'preseed'
      | 'technology'
      | 'capability'
      | 'queryResults'
      | 'customer'
      | 'parkedDomain';
    tabKeys?: string[];
    resource?: { key: string };
    defaultValues?: Record<string, string>;
  }) {
    // TEMPORARY FIX: Block 3rd drawer until N-drawer architecture is implemented
    // See N-DRAWER-REFACTOR.md for details
    if (resource) {
      // Opening a drawer (resource provided)
      const currentDrawerCount = drawerOrder.length;
      if (currentDrawerCount >= 2) {
        // Show toast and don't proceed
        toast.warning('Please close a drawer before opening another', {
          description: 'Only 2 drawers can be open at a time',
          duration: 3000,
        });
        return; // Don't open the 3rd drawer
      }
    }

    const drawerKey = `${drawerType}DrawerKey`;

    setSearchParams(
      searchParams => {
        // Read FRESH drawerOrder from the searchParams parameter to avoid stale closure
        const currentDrawerOrder = safeExecute<
          (
            | 'agent'
            | 'asset'
            | 'vulnerability'
            | 'seed'
            | 'integration'
            | 'threatIntel'
            | 'preseed'
            | 'technology'
            | 'capability'
            | 'queryResults'
            | 'customer'
            | 'parkedDomain'
          )[]
        >(() => {
          return JSON.parse(searchParams.get('drawerOrder') || '[]');
        }, []);

        if (resource) {
          searchParams.set(drawerKey, resource.key);

          // Check if we're on insights page and handle drawer replacement logic
          const isInsightsPage = window.location.pathname.includes('insights');
          let newDrawerOrder;

          if (isInsightsPage && currentDrawerOrder.includes('queryResults')) {
            // On insights page with query results drawer open, keep it as base and replace any second drawer
            if (drawerType === 'queryResults') {
              // If opening query results, just add it (or keep it if already open)
              newDrawerOrder = ['queryResults'];
            } else {
              // For any other drawer type, keep queryResults as first and replace any second drawer
              newDrawerOrder = ['queryResults', drawerType];
            }
          } else {
            // Default behavior: add to end of stack
            newDrawerOrder = [
              ...currentDrawerOrder.filter(drawer => drawer !== drawerType),
              drawerType,
            ];
          }

          searchParams.set('drawerOrder', JSON.stringify(newDrawerOrder));

          // Preserve existing tab params if not explicitly overridden in defaultValues
          // This prevents tab oscillation when navigating between rows with arrow keys
          tabKeys.forEach(key => {
            if (defaultValues?.[key] !== undefined) {
              searchParams.set(key, defaultValues[key]);
            } else {
              const existing = searchParams.get(key);
              // Not provided - preserve existing value if present, or default to empty string
              searchParams.set(key, existing || '');
            }
          });
        } else {
          [drawerKey, ...tabKeys].forEach(key => {
            searchParams.delete(key);
          });
          searchParams.set(
            'drawerOrder',
            JSON.stringify(
              currentDrawerOrder.filter(drawer => drawer !== drawerType)
            )
          );
        }

        return searchParams;
      },
      { replace: true }
    );
  }

  function handleOpenAgentDrawer(
    agent?: Agent,
    options?: { initialTab?: string }
  ) {
    handleSetSearchParams({
      drawerType: 'agent',
      tabKeys: ['agentDrawerTab'],
      resource: agent ? { key: agent.client_id } : undefined,
      defaultValues: options?.initialTab
        ? { agentDrawerTab: options.initialTab }
        : {},
    });
    setAgent(agent);
  }

  /**
   * Opens or closes the technology drawer
   *
   * CRITICAL: tabKeys is empty when opening to preserve sticky tab behavior.
   * When navigating between rows with keyboard, the tab should stay on the
   * user's selected tab, not reset. tabKeys only included when closing to
   * properly delete the tab param from URL.
   *
   * @param technology - Technology to open drawer for, or undefined to close
   */
  function handleOpenTechnologyDrawer(technology?: Technology) {
    handleSetSearchParams({
      drawerType: 'technology',
      // Only include tabKeys when CLOSING drawer (technology is undefined)
      // When OPENING drawer, leave tab param alone for sticky tab behavior
      tabKeys: technology ? [] : ['technologyDrawerTab'],
      resource: technology,
    });
  }

  function handleOpenAssetDrawer(
    asset?: PartialAsset,
    isActiveDirectory?: boolean
  ) {
    // For Active Directory entities, set specific tab defaults
    const defaultTabs: Record<string, string> = isActiveDirectory
      ? {
          assetDrawerTab: 'attributes', // Default to Attributes tab
          assetDrawerSubTab: 'activeDirectory', // Default to Active Directory sub-tab
          // The Classification view will be the default within the Active Directory section
        }
      : {};

    handleSetSearchParams({
      drawerType: 'asset',
      tabKeys: ['assetDrawerTab', 'assetDrawerSubTab', 'historyId'],
      resource: asset,
      defaultValues: defaultTabs,
    });
  }

  function handleOpenVulnerabilityDrawer(
    vulnerability?: Risk,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    doNotSetVulnerability?: boolean // Kept for backward compatibility
  ) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // VulnerabilityDrawer fetches data internally via useVulnerabilityDrawerData(key).
    // REMOVED: setVulnerability(vulnerability) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'vulnerability',
      tabKeys: ['vulnerabilityDrawerTab'],
      resource: vulnerability,
    });
  }

  function handleOpenSeedDrawer(seed?: Seed) {
    // KEY-BASED PATTERN: URL param (seedDrawerKey) is the single source of truth.
    // The SeedDrawer component fetches seed data internally via useSeedDrawerData(seedDrawerKey).
    // We no longer call setSeed() here to avoid race conditions when clicking quickly between rows.
    handleSetSearchParams({
      drawerType: 'seed',
      tabKeys: ['seedDrawerTab'],
      resource: seed,
    });
  }

  function handleOpenThreatIntelDrawer(threatIntel?: Vulnerability) {
    handleSetSearchParams({
      drawerType: 'threatIntel',
      resource: threatIntel,
      tabKeys: ['threat-intel-drawer-tab', 'cvss-version-selection'],
    });
    setThreatIntel(threatIntel);
  }

  function handleOpenPreseedDrawer(preseed?: Preseed) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // PreseedDrawer fetches data internally via usePreseedDrawerData(key).
    // REMOVED: setPreseed(preseed) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'preseed',
      tabKeys: ['preseedDrawerTab'],
      resource: preseed,
    });
  }

  function handleOpenCapabilityDrawer(capability?: Capability) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // CapabilityDrawer fetches data internally via useCapabilityDrawerData(key).
    // REMOVED: setCapability(capability) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'capability',
      tabKeys: ['capabilityDrawerTab'],
      resource: capability ? { key: capability.name } : undefined,
    });
  }

  function handleOpenCustomerDrawer(
    customer?: import('@/sections/customerManagement/types').CustomerData,
    options?: { initialTab?: string }
  ) {
    handleSetSearchParams({
      drawerType: 'customer',
      tabKeys: ['customerDrawerTab'],
      resource: customer ? { key: customer.email } : undefined,
      defaultValues: options?.initialTab
        ? { customerDrawerTab: options.initialTab }
        : {},
    });

    setCustomer(customer);
  }

  function handleOpenParkedDomainDrawer(parkedDomain?: ParkedDomain) {
    // KEY-BASED PATTERN: URL param is the single source of truth.
    // ParkedDomainDrawer fetches data internally via useParkedDomainDrawerData(key).
    // REMOVED: setParkedDomain(parkedDomain) to eliminate race conditions.
    handleSetSearchParams({
      drawerType: 'parkedDomain',
      tabKeys: [],
      resource: parkedDomain,
    });
  }

  function handleOpenIntegrationDrawer(
    integration?: IntegrationMeta,
    category?: string
  ) {
    if (category === undefined) {
      // Closing case
      setSearchParams(
        searchParams => {
          // Read FRESH drawerOrder from the searchParams parameter to avoid stale closure
          const currentDrawerOrder = safeExecute<string[]>(() => {
            return JSON.parse(searchParams.get('drawerOrder') || '[]');
          }, []);

          searchParams.delete(StorageKey.DRAWER_COMPOSITE_KEY);
          searchParams.delete('integrationDrawerCategory');
          // Remove integration from drawer order instead of deleting entirely
          searchParams.set(
            'drawerOrder',
            JSON.stringify(
              currentDrawerOrder.filter(drawer => drawer !== 'integration')
            )
          );
          return searchParams;
        },
        { replace: true }
      );
      setIntegration(undefined);
    } else {
      // Opening case (including default view)
      setSearchParams(
        searchParams => {
          // Read FRESH drawerOrder from the searchParams parameter to avoid stale closure
          const currentDrawerOrder = safeExecute<string[]>(() => {
            return JSON.parse(searchParams.get('drawerOrder') || '[]');
          }, []);

          searchParams.set(StorageKey.DRAWER_COMPOSITE_KEY, 'integration');
          if (category) {
            searchParams.set('integrationDrawerCategory', category);
          }
          searchParams.set(
            'drawerOrder',
            JSON.stringify([
              ...currentDrawerOrder.filter(drawer => drawer !== 'integration'),
              'integration',
            ])
          );
          return searchParams;
        },
        { replace: true }
      );
      setIntegration({} as IntegrationMeta);
    }
  }

  function handleOpenQueryResultsDrawer(
    results?: RiskWithVulnerability[],
    queryBlocks?: EntityBlock[],
    paginationControls?: {
      fetchNextPage: () => void;
      hasNextPage: boolean;
      isFetchingNextPage: boolean;
      totalCount?: number;
    },
    sortControls?: {
      orderBy: string;
      descending: boolean;
      onSortChange: (
        sortConfig: { orderBy: string; descending: boolean } | null
      ) => void;
    },
    refetchQuery?: () => void,
    collaborators?: Array<{ username: string; displayName?: string }>,
    includeCustomerColumn?: boolean
  ) {
    const key =
      results && results.length > 0 ? `query-results-${Date.now()}` : '';
    handleSetSearchParams({
      drawerType: 'queryResults',
      tabKeys: ['queryResultsDrawerKey'],
      resource: { key },
    });
    setQueryResults(results);
    setQueryBlocks(queryBlocks);
    setQueryPaginationControls(paginationControls);
    setQuerySortControls(sortControls);
    setQueryRefetch(() => refetchQuery);
    setQueryCollaborators(collaborators);
    setIncludeCustomerColumn(includeCustomerColumn ?? false);
  }

  /**
   * Internal helper for setting drawer tab and key values using functional setter pattern.
   *
   * CRITICAL: This uses the functional setter pattern to avoid stale closure issues
   * that cause tab alternation when mixing mouse clicks and keyboard navigation.
   *
   * @see https://reactrouter.com/en/main/hooks/use-search-params - Functional updates
   * @param paramName - The URL param name (e.g., 'seedDrawerTab', 'assetDrawerKey')
   * @param value - The value to set
   */
  function setDrawerTabInternal(paramName: string, value: string) {
    setSearchParams(
      params => {
        params.set(paramName, value);
        return params;
      },
      { replace: true }
    );
  }

  function setAgentDrawerTab(tab: string) {
    setDrawerTabInternal('agentDrawerTab', tab);
  }

  function setSeedDrawerTab(tab: string) {
    setDrawerTabInternal('seedDrawerTab', tab);
  }

  function setAssetDrawerTab(tab: string) {
    setDrawerTabInternal('assetDrawerTab', tab);
  }

  function setTechnologyDrawerTab(tab: string) {
    setDrawerTabInternal('technologyDrawerTab', tab);
  }

  function setVulnerabilityDrawerTab(tab: string) {
    setDrawerTabInternal('vulnerabilityDrawerTab', tab);
  }

  function setCustomerDrawerTab(tab: string) {
    setDrawerTabInternal('customerDrawerTab', tab);
  }

  const handleRiskNotification = useCallback(
    (value: GlobalState['riskNotification']['value']) => {
      setRiskNotification(value);

      // Automatically close the notification after 3 seconds (optional)
      setTimeout(() => {
        setRiskNotification(null);
      }, 3000);
    },
    []
  );

  // Memoize the keyboardListeners object to prevent recreating it on every render
  const keyboardListeners = useMemo(
    () => ({
      add: addKeyDownListeners,
      remove: removeKeyDownListeners,
      poeEnabled,
      setPOEEnabled,
    }),
    [addKeyDownListeners, removeKeyDownListeners, poeEnabled]
  );

  return (
    <GlobalStateContext.Provider
      value={{
        lastVisitedUserId,
        setLastVisitedUserId,
        drawerState: {
          exposuresDrawerOpen,
          setExposuresDrawerOpen,
          notificationsDrawerOpen,
          notificationsDrawerFilterType,
          setNotificationsDrawerOpen,
          drawerOrder,
          agent,
          seed,
          vulnerability,
          integration,
          threatIntel,
          preseed,
          capability,
          customer,
          parkedDomain,
          queryResults,
          queryBlocks,
          queryPaginationControls,
          querySortControls,
          queryRefetch,
          queryCollaborators,
          includeCustomerColumn,
          agentDrawerKey,
          agentDrawerTab,
          assetDrawerTab,
          assetDrawerSubTab,
          assetDrawerKey,
          technologyDrawerKey,
          technologyDrawerTab,
          vulnerabilityDrawerTab,
          vulnerabilityDrawerKey,
          threatIntelDrawerKey,
          preseedDrawerKey,
          preseedDrawerTab,
          seedDrawerKey,
          seedDrawerTab,
          capabilityDrawerKey,
          capabilityDrawerTab,
          customerDrawerKey,
          customerDrawerTab,
          parkedDomainDrawerKey,
          queryResultsDrawerKey,
          setAgent,
          setAgentDrawerTab,
          setSeedDrawerTab,
          setSeed,
          handleOpenSeedDrawer,
          handleOpenAgentDrawer,

          setAssetDrawerTab,
          setTechnologyDrawerTab,
          setVulnerabilityDrawerTab,
          setVulnerability,
          setThreatIntel,
          setPreseed,
          setCapability,
          setCustomer,
          setCustomerDrawerTab,
          setParkedDomain,
          setIntegration,
          setIntegrationDrawerCategory: category => {
            setSearchParams(params => {
              params.set('integrationDrawerCategory', category);
              return params;
            });
          },
          setQueryResults,
          setQueryBlocks,
          setQueryPaginationControls,
          setQuerySortControls,
          handleOpenQueryResultsDrawer,

          handleOpenTechnologyDrawer,
          handleOpenAssetDrawer,
          handleOpenVulnerabilityDrawer,
          handleOpenIntegrationDrawer,
          handleOpenThreatIntelDrawer,
          handleOpenPreseedDrawer,
          handleOpenCapabilityDrawer,
          handleOpenCustomerDrawer,
          handleOpenParkedDomainDrawer,
        },
        dashboard: {
          period,
        },
        modal: {
          selectUserAgent: {
            assetKeys: selectUserAgentAssetKeys,
            onAssetKeysChange: setSelectUserAgentAssetKeys,
          },
          ticket: {
            associateTicket: {
              open: associateTicketOpen,
              onOpenChange: setAssociateTicketOpen,
              riskKeys: associateTicketRiskKeys,
              onRiskKeysChange: setAssociateTicketRiskKeys,
            },
            setup: { open: setupOpen, onOpenChange: setSetupOpen },
          },
          alert: { open: alertOpen, onOpenChange: setAlertOpen },
          upgrade: {
            open: upgradeOpen,
            onOpenChange: setUpgradeOpen,
          },
          seed: { open: seedOpen, onOpenChange: setSeedOpen },
          risk: {
            open: riskOpen,
            type: riskType,
            onChange: (open, type) => {
              setRiskOpen(open);
              setRiskType(type);
            },
            selectedAssets,
            onSelectedAssetsChange: setSelectedAssets,
            selectedRisks,
            onSelectedRisksChange: setSelectedRisks,
          },
          asset: { open: assetOpen, onOpenChange: setAssetOpen },
          file: { open: fileOpen, onOpenChange: setFileOpen },
          pushNotification: {
            open: pushNotificationOpen,
            onOpenChange: setPushNotificationOpen,
          },
          surfaceSetup: {
            open: surfaceSetupOpen,
            onOpenChange: setSurfaceSetupOpen,
          },
          allModal: { open: allModalOpen, onOpenChange: setAllModalOpen },
        },
        awsMarketplaceConfig: {
          value: awsMarketplaceConfig,
          onChange: setAwsMarketplaceConfig,
          verifyLinking,
          onVerifyLinkingChange: setVerifyLinking,
        },
        riskNotification: {
          value: riskNotification,
          onChange: handleRiskNotification,
        },
        setupIntegration: {
          value: setupIntegration,
          onChange: setSetupIntegration,
        },
        keyboardListeners,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};
